import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Mail, MailOpen, Clock, CalendarDays, CalendarClock, Inbox, RefreshCw } from 'lucide-react';
import BBALoader from '../BBALoader';
import CrmStatCard from './CrmStatCard';
import { fetchGmailMessagesWithStatus } from '../../services/gmailService';
import { fetchGoogleCalendarEvents } from '../../services/googleCalendarService';
import { getUserIntegrationsStatus } from '../../services/userIntegrationService';
import { loadMeetingSummariesForContext } from '../../utils/meetingSummaryLookup';
import { canonicalCalendarEmailKey } from '../../utils/clientCalendarEventMatching';
import {
  ENGAGEMENT_TIMEFRAMES,
  buildInternalMatcher,
  computeMemberEmailStats,
  computeMemberMeetingStats,
  computeMemberPortalStats,
  dedupeMessages,
  engagementRoster,
  formatDaysAgo,
  formatDurationShort,
  lastContactAt,
  memberEmailIdentity,
} from '../../utils/memberEngagementMetrics';
import { db } from '../../firebase';

const GMAIL_HEADER_LIMIT = 500;
const UPCOMING_DAYS = 60;
const QUIET_AFTER_DAYS = 21;

const shortDate = (d) => (d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—');

/** Weighted mean of per-member averages, so one chatty member doesn't vanish. */
function pooledAverage(statsList, avgKey, countKey) {
  let total = 0;
  let count = 0;
  statsList.forEach((s) => {
    if (s[avgKey] != null && s[countKey]) {
      total += s[avgKey] * s[countKey];
      count += s[countKey];
    }
  });
  return count ? total / count : null;
}

const StatLine = ({ label, value, hint, tone }) => (
  <div className="flex items-baseline justify-between gap-3 py-1">
    <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
    <span className="text-right">
      <span
        className={`text-sm font-semibold ${
          tone === 'warn' ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-white'
        }`}
      >
        {value}
      </span>
      {hint && <span className="ml-1 text-[11px] text-gray-400 dark:text-gray-500">{hint}</span>}
    </span>
  </div>
);

const Section = ({ emoji, title, children }) => (
  <div className="rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-3">
    <p className="mb-1 text-center text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
      <span className="mr-1" aria-hidden="true">{emoji}</span>
      {title}
    </p>
    {children}
  </div>
);

/**
 * Access tab ▸ Team Engagement: per client team member, email volume + reply times (every
 * connected staff Gmail), meetings (every connected staff Google Calendar + Fathom recordings)
 * and portal usage (the same rows the Portal Activity list shows).
 */
const MemberEngagementPanel = ({ account, members, internalUsers, portalActivityRows }) => {
  const [timeframeDays, setTimeframeDays] = useState(90);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [events, setEvents] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [coverage, setCoverage] = useState({ gmail: [], calendar: [], cappedMailboxes: [] });
  const [refreshKey, setRefreshKey] = useState(0);

  const roster = useMemo(() => engagementRoster(account, members), [account, members]);
  const identities = useMemo(
    () => new Map(roster.map((m) => [m.id, memberEmailIdentity(m, account)])),
    [roster, account]
  );
  const allAddresses = useMemo(
    () => [...new Set([...identities.values()].flatMap((i) => i.addresses))],
    [identities]
  );
  const isInternal = useMemo(() => buildInternalMatcher(internalUsers), [internalUsers]);
  const staffNameByKey = useMemo(() => {
    const map = new Map();
    (internalUsers || []).forEach((u) => {
      const key = canonicalCalendarEmailKey(u.email);
      if (key) map.set(key, u.name);
    });
    return map;
  }, [internalUsers]);

  const load = useCallback(async () => {
    const staff = (internalUsers || []).filter((u) => u.id);
    if (!allAddresses.length || !staff.length) return;
    setLoading(true);
    try {
      const now = new Date();
      const since = new Date(now.getTime() - timeframeDays * 86400000);
      const until = new Date(now.getTime() + UPCOMING_DAYS * 86400000);
      const addressQuery = allAddresses.map((a) => `from:${a} OR to:${a} OR cc:${a}`).join(' OR ');
      const gmailQuery = `(${addressQuery}) newer_than:${timeframeDays}d`;

      const perStaff = await Promise.all(
        staff.map(async (user) => {
          let integrations = {};
          try {
            integrations = (await getUserIntegrationsStatus(user.id)) || {};
          } catch {
            /* treat as not connected */
          }
          const gmailConnected = integrations.gmail?.connected === true;
          const calendarConnected = integrations.googleCalendar?.connected === true;

          const [gmail, calendarEvents] = await Promise.all([
            gmailConnected
              ? fetchGmailMessagesWithStatus(user.id, gmailQuery, GMAIL_HEADER_LIMIT, { format: 'metadata' })
              : Promise.resolve({ messages: [], status: 'not_connected' }),
            calendarConnected ? fetchGoogleCalendarEvents(user.id, since, until) : Promise.resolve([]),
          ]);
          return { user, gmail, calendarEvents, calendarConnected };
        })
      );

      const eventsById = new Map();
      perStaff.forEach(({ calendarEvents }) => {
        (calendarEvents || []).forEach((ev) => {
          const key = ev.googleEventId || ev.id;
          if (key && !eventsById.has(key)) eventsById.set(key, ev);
        });
      });

      let loadedSummaries = [];
      try {
        loadedSummaries = await loadMeetingSummariesForContext(db, {
          accountId: account?.id,
          recorderUids: staff.map((u) => u.id),
        });
      } catch (err) {
        console.warn('Team Engagement: could not load meeting summaries', err);
      }

      setMessages(dedupeMessages(perStaff.flatMap(({ gmail }) => gmail.messages || [])));
      setEvents([...eventsById.values()]);
      setSummaries(loadedSummaries);
      setCoverage({
        gmail: perStaff.map(({ user, gmail }) => ({ name: user.name, status: gmail.status })),
        calendar: perStaff.map(({ user, calendarConnected }) => ({ name: user.name, connected: calendarConnected })),
        cappedMailboxes: perStaff
          .filter(({ gmail }) => (gmail.messages || []).length >= GMAIL_HEADER_LIMIT)
          .map(({ user }) => user.name),
      });
    } finally {
      setLoading(false);
    }
  }, [account?.id, allAddresses, internalUsers, timeframeDays]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const now = useMemo(() => new Date(), [messages, events]); // eslint-disable-line react-hooks/exhaustive-deps
  const since = useMemo(() => new Date(now.getTime() - timeframeDays * 86400000), [now, timeframeDays]);

  const rows = useMemo(
    () =>
      roster.map((member) => {
        const { keys } = identities.get(member.id) || { keys: new Set() };
        const email = computeMemberEmailStats(messages, keys, isInternal, { since, now });
        const meetings = computeMemberMeetingStats(events, summaries, keys, { since, now });
        const portal = computeMemberPortalStats(portalActivityRows, member.id, { since });
        return { member, email, meetings, portal, lastContact: lastContactAt(email, meetings) };
      }),
    [roster, identities, messages, events, summaries, portalActivityRows, isInternal, since, now]
  );

  const totals = useMemo(() => {
    const emails = rows.map((r) => r.email);
    return {
      sent: emails.reduce((s, e) => s + e.sent, 0),
      received: emails.reduce((s, e) => s + e.received, 0),
      ourReply: pooledAverage(emails, 'avgOurReplyMs', 'ourReplies'),
      theirReply: pooledAverage(emails, 'avgTheirReplyMs', 'theirReplies'),
      awaiting: emails.reduce((s, e) => s + e.awaitingReply, 0),
      held: rows.reduce((s, r) => s + r.meetings.held, 0),
      upcoming: rows.reduce((s, r) => s + r.meetings.upcoming, 0),
    };
  }, [rows]);

  const gmailConnected = coverage.gmail.filter((g) => g.status === 'ok');
  const gmailMissing = coverage.gmail.filter((g) => g.status !== 'ok');
  const calendarConnected = coverage.calendar.filter((c) => c.connected);

  if (!roster.length) return null;

  return (
    <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-600">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 text-center">Team Engagement</h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-4">
        Emails, meetings and portal use for each person on the account, from every teammate's connected Gmail and Google Calendar plus Fathom recordings.
      </p>

      <div className="flex flex-wrap items-center justify-center gap-2 mb-5">
        {ENGAGEMENT_TIMEFRAMES.map((tf) => (
          <button
            key={tf.days}
            type="button"
            onClick={() => setTimeframeDays(tf.days)}
            className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
              timeframeDays === tf.days
                ? 'bg-red-600 border-red-600 text-white'
                : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            {tf.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setRefreshKey((k) => k + 1)}
          disabled={loading}
          className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300 disabled:opacity-50 transition-colors"
          title="Refresh"
          aria-label="Refresh team engagement"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading ? (
        <div className="flex flex-col items-center gap-2 py-8">
          <BBALoader size={28} />
          <p className="text-xs text-gray-500 dark:text-gray-400">Reading teammates' mailboxes and calendars…</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
            <CrmStatCard compact icon={Inbox} tone="blue" label="Emails from client" value={totals.sent} />
            <CrmStatCard compact icon={Mail} tone="purple" label="Emails to client" value={totals.received} />
            <CrmStatCard
              compact
              icon={Clock}
              tone="green"
              label="Our avg reply"
              value={formatDurationShort(totals.ourReply)}
              hint={totals.theirReply != null ? `Theirs ${formatDurationShort(totals.theirReply)}` : undefined}
            />
            <CrmStatCard
              compact
              icon={MailOpen}
              tone={totals.awaiting ? 'orange' : 'gray'}
              label="Awaiting our reply"
              value={totals.awaiting}
            />
            <CrmStatCard compact icon={CalendarDays} tone="red" label="Meetings held" value={totals.held} />
            <CrmStatCard compact icon={CalendarClock} tone="yellow" label="Upcoming" value={totals.upcoming} />
          </div>

          <div className="space-y-4">
            {rows.map(({ member, email, meetings, portal, lastContact }) => {
              const quiet = !lastContact || now - lastContact > QUIET_AFTER_DAYS * 86400000;
              const topStaff = email.staff
                .slice(0, 2)
                .map((s) => `${staffNameByKey.get(canonicalCalendarEmailKey(s.email)) || s.email} (${s.count})`)
                .join(', ');
              return (
                <div
                  key={member.id}
                  className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-4"
                >
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-10 w-10 shrink-0 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center overflow-hidden ring-1 ring-gray-200 dark:ring-gray-700">
                        {member.profileImage ? (
                          <img src={member.profileImage} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-300">
                            {(member.name || member.email || '?').charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">
                          {member.name || member.email}
                          {member.isPrimary && (
                            <span className="ml-2 align-middle rounded-full border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-300">
                              Primary
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{member.email}</p>
                      </div>
                    </div>
                    <span
                      className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${
                        quiet
                          ? 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                          : 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                      }`}
                      title={lastContact ? lastContact.toLocaleString() : 'No email or meeting in this timeframe'}
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
                      Last contact {formatDaysAgo(lastContact, now)}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Section emoji="✉️" title="Email">
                      <StatLine label="Sent to us" value={email.sent} hint={email.lastSentAt ? `last ${shortDate(email.lastSentAt)}` : null} />
                      <StatLine label="Received from us" value={email.received} hint={email.lastReceivedAt ? `last ${shortDate(email.lastReceivedAt)}` : null} />
                      <StatLine
                        label="Our avg reply"
                        value={formatDurationShort(email.avgOurReplyMs)}
                        hint={email.ourReplies ? `median ${formatDurationShort(email.medianOurReplyMs)}` : null}
                      />
                      <StatLine
                        label="Their avg reply"
                        value={formatDurationShort(email.avgTheirReplyMs)}
                        hint={email.theirReplies ? `median ${formatDurationShort(email.medianTheirReplyMs)}` : null}
                      />
                      <StatLine
                        label="Awaiting our reply"
                        value={email.awaitingReply}
                        hint={email.awaitingSince ? `since ${shortDate(email.awaitingSince)}` : null}
                        tone={email.awaitingReply ? 'warn' : undefined}
                      />
                      {topStaff && (
                        <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500 truncate" title={topStaff}>
                          Mostly with {topStaff}
                        </p>
                      )}
                    </Section>

                    <Section emoji="📅" title="Meetings">
                      <StatLine
                        label="Held"
                        value={meetings.held}
                        hint={meetings.hoursMet ? `${meetings.hoursMet.toFixed(1)}h` : null}
                      />
                      <StatLine label="Recorded (Fathom)" value={meetings.recorded} />
                      <StatLine label="Last met" value={shortDate(meetings.last?.at)} />
                      <StatLine label="Upcoming" value={meetings.upcoming} hint={meetings.next ? `next ${shortDate(meetings.next.at)}` : null} />
                      {meetings.declined > 0 && <StatLine label="Declined" value={meetings.declined} tone="warn" />}
                      {meetings.next && (
                        <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500 truncate" title={meetings.next.title}>
                          Next: {meetings.next.title}
                        </p>
                      )}
                    </Section>

                    <Section emoji="🖥️" title="Portal">
                      {member.id === 'account-primary' ? (
                        <p className="py-2 text-center text-xs text-gray-400 dark:text-gray-500">No portal account yet.</p>
                      ) : (
                        <>
                          <StatLine label="Logins" value={portal.logins} />
                          <StatLine label="Page views" value={portal.pageViews} />
                          <StatLine label="Last active" value={formatDaysAgo(portal.lastActiveAt, now)} />
                          {portal.topPage && (
                            <StatLine label="Most viewed" value={portal.topPage.label} hint={`×${portal.topPage.count}`} />
                          )}
                        </>
                      )}
                    </Section>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">
            Gmail: {gmailConnected.length} of {coverage.gmail.length} teammates connected
            {gmailMissing.length > 0 && ` (missing ${gmailMissing.map((g) => g.name).join(', ')})`}
            {' · '}Calendar: {calendarConnected.length} of {coverage.calendar.length}
            {coverage.cappedMailboxes.length > 0 &&
              ` · Latest ${GMAIL_HEADER_LIMIT} emails only for ${coverage.cappedMailboxes.join(', ')}`}
            . Replies slower than 14 days count as new conversations.
          </p>
        </>
      )}
    </div>
  );
};

export default MemberEngagementPanel;
